import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Link,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { SelectChangeEvent } from '@mui/material';
import { fetchDomainDetail, updateDomain, type DomainDetail } from '../services/domainsApi';
import { CustomerOwnerSelect } from '../components/domains/CustomerOwnerSelect';

const REPAIRSHOPR_BASE = 'https://techsolvers.repairshopr.com';
const ACTION_OPTIONS = ['renew', 'lapse'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Grid>
);

export const DomainDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const domainId = location.pathname.match(/^\/domain\/(.+)$/)?.[1] ?? '';

  const [domain, setDomain] = useState<DomainDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setDomain(await fetchDomainDetail(domainId, signal));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load domain');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const save = useCallback(
    async (update: { autoRenew?: boolean; actionOnExpiry?: string }, successMsg: string) => {
      if (!domain) return;
      setSaving(true);
      try {
        const updated = await updateDomain(domain.id, update);
        setDomain(updated);
        setSnackbar({ open: true, message: successMsg, severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Update failed', severity: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [domain],
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!domain) return null;

  const actionValue = ACTION_OPTIONS.includes((domain.actionOnExpiry || '').toLowerCase())
    ? (domain.actionOnExpiry || '').toLowerCase()
    : '';

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>
      <Typography variant="h6" gutterBottom>{domain.name}</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Field label="Status">
            {domain.status ? <Chip label={domain.status} size="small" /> : '—'}
          </Field>
          <Field label="Expiry">{fmtDate(domain.expiry)}</Field>
          <Field label="Created">{fmtDate(domain.createdDate)}</Field>
          <Field label="Owner / Business">
            {domain.businessName ?? (domain.hasOwner ? '—' : 'No owner')}
          </Field>
          <Field label="Registrant Email">{domain.registrantEmail ?? '—'}</Field>
          <Field label="TechSolvers owned">{domain.techsolversOwned ? 'Yes' : 'No'}</Field>
          <Field label="Last Invoice">
            {domain.invoice ? (
              <>
                {domain.invoice.number && (
                  <Link component="button" type="button" underline="hover" sx={{ mr: 1 }}
                    onClick={() => navigate(`/invoice/${domain.invoice!.number}`)}>
                    {domain.invoice.number}
                  </Link>
                )}
                {domain.invoice.rsId && (
                  <Link href={`${REPAIRSHOPR_BASE}/invoices/${domain.invoice.rsId}`} target="_blank" rel="noopener noreferrer" underline="hover">
                    RS
                  </Link>
                )}
                {domain.invoice.isPaid ? ' (paid)' : ' (unpaid)'}
              </>
            ) : '—'}
          </Field>
          <Field label="Last Paid">{fmtDate(domain.lastPaidDate)}</Field>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Edit</Typography>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={domain.autoRenew}
                disabled={saving}
                onChange={(e) => save({ autoRenew: e.target.checked }, `Auto-renew ${e.target.checked ? 'enabled' : 'disabled'}`)}
              />
            }
            label="Auto-renew"
          />
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={saving}>
            <InputLabel id="action-on-expiry-label">Action on expiry</InputLabel>
            <Select
              labelId="action-on-expiry-label"
              label="Action on expiry"
              value={actionValue}
              onChange={(e: SelectChangeEvent) => save({ actionOnExpiry: e.target.value }, `Action on expiry set to ${e.target.value}`)}
            >
              {ACTION_OPTIONS.map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {saving && <CircularProgress size={20} />}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
            Owner
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {domain.businessName ?? (domain.hasOwner ? '—' : 'No owner')}
          </Typography>
          <CustomerOwnerSelect
            domainId={domain.id}
            currentOwnerGuid={domain.ownerCustomerGuid}
            onReassigned={(updated) => {
              setDomain(updated);
              setSnackbar({
                open: true,
                message: `Owner set to ${updated.businessName ?? 'customer'}`,
                severity: 'success',
              });
            }}
          />
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
